// Nástěnka kontaktů v Sales (mockup „Nástěnka kontaktů“, varianta A, schváleno 1. 10. 2026).
// Sloupce jako v Notionu: Odloženo, Nové, Nedovoláno 1 až 3, Trial. Na počítači sloupce
// vedle sebe, na telefonu mřížka sloupců s počty (tlačítka) a pod ní jen vybraný sloupec.
// Přetahování mezi sloupci schválně není: stav se mění akcemi v kartě kontaktu, aby odešly
// správné SMS a počítaly se pokusy.
import React, { useMemo, useState } from "react";
import { Phone, Mail, Megaphone } from "lucide-react";
import { Pil, SpolupracePil } from "./salesUi.jsx";
import { kdyText, casBezNuly, denKratce, telefonHezky } from "./salesApi.js";
import { ton as tonPodleKlice } from "../../lib/tony.js";

// 2. 10. 2026 (Láďa): Odloženo jako první, zkušební tréninky ve sloupci Trial.
const SLOUPCE = [
  { key: "odlozeno", nazev: "Odloženo", ton: "info" },
  { key: "novy", nazev: "Nové", ton: "znacka" },
  { key: "p1", nazev: "Nedovoláno 1", ton: "pozor" },
  { key: "p2", nazev: "Nedovoláno 2", ton: "pozor" },
  { key: "p3", nazev: "Nedovoláno 3", ton: "problem" },
  { key: "zkusebni", nazev: "Trial", ton: "ok" },
];
// Při hledání se najdou i uzavřené a staré kontakty, ty dostanou vlastní sloupec.
// 7. 10. 2026 (#139): výsledek jako v Notionu, sloupce se ukážou, jen když v nich někdo je
// (pohled Uzavřené nebo hledání).
const VYSLEDEK = [
  { key: "deal", nazev: "Deal", ton: "znacka" },
  { key: "prohra", nazev: "Prohra", ton: "problem" },
  { key: "nqlead", nazev: "NQlead", ton: "neutralni" },
];
const OSTATNI = { key: "ostatni", nazev: "Ostatní", ton: "neutralni" };

function sloupecKontaktu(k) {
  if (k.stav === "novy") return "novy";
  if (k.stav === "pokus") return `p${Math.min(3, Math.max(1, Number(k.pokus) || 1))}`;
  if (k.stav === "odlozeno" || k.stav === "doplnit") return "odlozeno";
  if (k.stav === "zkusebni") return "zkusebni";
  if (k.stav === "klientka") return "deal";
  if (k.stav === "neuzavreno") return "prohra";
  if (k.stav === "nezajem") return "nqlead";
  return "ostatni";
}
const klicRazeni = (k) => (k.stav === "zkusebni" && k.zkusebni ? k.zkusebni.datum : k.dalsi) || "";

/** Pilulka, která se na úzké kartě zkrátí (e‑mail), místo aby přetekla. */
function PilZkracena({ ikona: Ikona, children }) {
  const t = tonPodleKlice("neutralni");
  return (
    <span className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-bold ${t.pilulka}`}>
      <Ikona className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  );
}

function Karta({ k, onOtevrit }) {
  let kdy = null;
  if (k.stav === "zkusebni" && k.zkusebni) kdy = <Pil ton="ok" casti={["zkušební", `${denKratce(k.zkusebni.datum)} ${casBezNuly(k.zkusebni.cas || "")}`.trim()]} />;
  else if (k.stav === "doplnit") kdy = <Pil ton="pozor" casti="doplnit datum" />;
  else if (k.dalsi) kdy = <Pil ton="neutralni" casti={["další kontakt", `${kdyText(k.dalsi)}${k.dalsi_cas ? ` ${casBezNuly(k.dalsi_cas)}` : ""}`]} />;
  else if (k.stav === "novy") kdy = <Pil ton="znacka" casti="naplánuj hovor" />;
  return (
    <button type="button" onClick={() => onOtevrit(k.id)} aria-label={`Otevřít kartu ${k.jmeno}`}
      className="block w-full rounded-xl border border-slate-200 bg-white p-3 text-left shadow-glass transition hover:border-brand/40 active:bg-brand-light/60">
      <div className="text-base font-bold leading-snug text-slate-900">{k.jmeno}</div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {kdy}
        <SpolupracePil k={k} />
        {k.telefon && <Pil ton="neutralni" ikona={Phone} casti={telefonHezky(k.telefon)} />}
        {k.email && <PilZkracena ikona={Mail}>{k.email}</PilZkracena>}
        {k.zdroj && <Pil ton="neutralni" ikona={Megaphone} casti={k.zdroj} />}
      </div>
    </button>
  );
}

function Prazdny() {
  return <div className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-center text-[13px] text-slate-500">Nikdo</div>;
}

export default function NastenkaKontaktu({ seznam = [], uzka = false, onOtevrit }) {
  const sloupce = useMemo(() => {
    const m = new Map([...SLOUPCE, ...VYSLEDEK, OSTATNI].map((s) => [s.key, []]));
    for (const k of seznam) m.get(sloupecKontaktu(k)).push(k);
    for (const arr of m.values()) arr.sort((a, b) => klicRazeni(a).localeCompare(klicRazeni(b)));
    const vse = [...SLOUPCE, ...VYSLEDEK.filter((s) => m.get(s.key).length), ...(m.get("ostatni").length ? [OSTATNI] : [])];
    return vse.map((s) => ({ ...s, kontakty: m.get(s.key) }));
  }, [seznam]);
  const prvniNeprazdny = sloupce.find((s) => s.kontakty.length)?.key || "novy";
  const [vybrany, setVybrany] = useState(null);
  const aktivni = sloupce.find((s) => s.key === vybrany) || sloupce.find((s) => s.key === prvniNeprazdny);

  if (uzka) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {sloupce.map((s) => {
            const zapnuto = s.key === aktivni.key;
            return (
              <button key={s.key} type="button" onClick={() => setVybrany(s.key)} aria-pressed={zapnuto}
                className={`min-h-[64px] rounded-2xl border bg-white px-2.5 py-2 text-left shadow-glass transition ${zapnuto ? "border-brand bg-brand-light ring-2 ring-brand/25" : "border-slate-200"}`}>
                <span className={`block text-[13px] font-bold leading-tight ${tonPodleKlice(s.ton).text}`}>{s.nazev}</span>
                <span className="mt-0.5 block text-xl font-bold tabular-nums text-slate-900">{s.kontakty.length}</span>
              </button>
            );
          })}
        </div>
        <div className="space-y-2 rounded-2xl bg-brand-light/60 p-2.5">
          <div className="flex items-center gap-2 px-1"><Pil ton={aktivni.ton} casti={aktivni.nazev} /><span className="ml-auto text-[13px] font-bold tabular-nums text-slate-500">{aktivni.kontakty.length}</span></div>
          {aktivni.kontakty.length ? aktivni.kontakty.map((k) => <Karta key={k.id} k={k} onOtevrit={onOtevrit} />) : <Prazdny />}
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {sloupce.map((s) => (
        <section key={s.key} aria-label={s.nazev} className="w-[264px] shrink-0 space-y-2 self-start rounded-2xl bg-brand-light/60 p-2.5">
          <div className="flex items-center gap-2 px-1"><Pil ton={s.ton} casti={s.nazev} /><span className="ml-auto text-[13px] font-bold tabular-nums text-slate-500">{s.kontakty.length}</span></div>
          {s.kontakty.length ? s.kontakty.map((k) => <Karta key={k.id} k={k} onOtevrit={onOtevrit} />) : <Prazdny />}
        </section>
      ))}
    </div>
  );
}
